import React from 'react';
import { GnEventType, recordGnEvent } from './rum-events';

let renderCount = 0;

export function measureMount(fn: () => void): void {
  renderCount = 0;
  performance.mark('gn:connectedCallback:start');
  fn();
  performance.mark('gn:connectedCallback:end');
  const measure = performance.measure('gn:mount', 'gn:connectedCallback:start', 'gn:connectedCallback:end');
  recordGnEvent('gn.mount', { durationMs: Math.round(measure.duration) });
  performance.clearMarks('gn:connectedCallback:start');
  performance.clearMarks('gn:connectedCallback:end');
}

export function startRender(): number {
  if (renderCount > 0) {
    performance.clearMarks(`gn:render:start:${renderCount}`);
  }
  renderCount++;
  const count = renderCount;
  performance.mark(`gn:render:start:${count}`);
  return count;
}

export function emitRenderTiming(count: number): void {
  const startMark = `gn:render:start:${count}`;
  const endMark = `gn:render:end:${count}`;
  performance.mark(endMark);
  try {
    const measure = performance.measure(`gn:render:${count}`, startMark, endMark);
    recordGnEvent('gn.render', { durationMs: Math.round(measure.duration), renderCount: count });
  } catch {
    // marks may not exist if render was skipped
  }
  performance.clearMarks(startMark);
  performance.clearMarks(endMark);
}

export function recordUnmount(): void {
  recordGnEvent('gn.unmount', {});
}

export async function measureAsync<T>(
  eventType: GnEventType,
  metadata: Record<string, unknown>,
  fn: () => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  const start = performance.now();
  try {
    const result = await fn();
    if (signal?.aborted) return result;
    recordGnEvent(eventType, {
      ...metadata,
      durationMs: Math.round(performance.now() - start),
    });
    return result;
  } catch (err) {
    if (signal?.aborted) throw err;
    recordGnEvent(eventType, {
      ...metadata,
      durationMs: Math.round(performance.now() - start),
      error: true,
    });
    throw err;
  }
}

export function RenderTimingProbe({ onCommit }: { onCommit: () => void }) {
  const onCommitRef = React.useRef(onCommit);
  onCommitRef.current = onCommit;
  React.useLayoutEffect(() => {
    onCommitRef.current();
  }, []);
  return null;
}

export function _resetPerfForTesting(): void {
  renderCount = 0;
}
